
import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ButtonLoadingDirective } from '@app/shared/directives/button-loading/button-loading.directive';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { zip as observableZip } from 'rxjs';
import { LookupPlusService } from '../../services/lookups/lookup-plus.service';
import { ProjectCustomEndpointService } from '../../services/project-custom-endpoint.service';
import { ProjectEstimateService } from '../../services/project-estimate.service';
import { ProjectWellHookupParameter } from './../../../../shared/models/project-well-hookup-parameter';

@Component({
  selector: 'app-project-well-hookup-details',
  templateUrl: './project-well-hookup-details.component.html',
  styleUrls: ['./project-well-hookup-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ButtonDirective,
    ButtonLoadingDirective,
    DropDownListComponent,
    ReactiveFormsModule,
  ],
})
export class ProjectWellHookupDetailsComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  private lookupPlusService = inject(LookupPlusService);
  private projectCustomEndpointService = inject(ProjectCustomEndpointService);
  private estimationService = inject(ProjectEstimateService);

  public wellHookupForm: UntypedFormGroup;
  public dCellarPadVisitTypes: any[] = [];
  public dTankBatteryDesigns: any[] = [];
  public wellHookupParameters: ProjectWellHookupParameter;
  public yesNoResponse: Array<any> = [{ text: 'Yes', value: true }, { text: 'No', value: false }];
  public col1fxFlex: number = 50;
  public col2fxFlex: number = 50;
  public loading: boolean = false;
  public icons = kendoIcons;

  readonly tabTdv = output<boolean>();
  readonly iProjectId = input<number>(undefined);
  readonly iProjectTypeId = input<number>(undefined);
  readonly iProjectStatusId = input<number>(undefined);

  ngOnInit() {
    this.generateForm();
    this.getCellarPadVisitTypes();
    this.getTankBatteryDesigns();
    this.getWellHookupParameters(false);
  }

  public updateWellHookupParameters(e): void {
    const requests = [];
    this.wellHookupParameters.CellarPadVisitTypeId = this.wellHookupForm.controls.CellarPadVisitTypeId.value;
    this.wellHookupParameters.AssociatedTankBatteryDesignId = this.wellHookupForm.controls.AssociatedTankBatteryDesignId.value;
    this.wellHookupParameters.AboveGradeWellCount = this.wellHookupForm.controls.AboveGradeWellCount.value;
    this.wellHookupParameters.BelowGradeWellCount = this.wellHookupForm.controls.BelowGradeWellCount.value;
    this.wellHookupParameters.IsLocatedOnTankBatteryPad = this.wellHookupForm.controls.IsLocatedOnTankBatteryPad.value;
    if (this.wellHookupForm.dirty && this.wellHookupForm.controls.WellToEdgeOfPadDistance.value == null) {
      this.wellHookupForm.controls.WellToEdgeOfPadDistance.setValue(0);
    }
    this.wellHookupParameters.WellToEdgeOfPadDistance = this.wellHookupForm.controls.WellToEdgeOfPadDistance.value;
    requests.push(this.projectCustomEndpointService.patch(`ProjectWellHookupParameters(${this.wellHookupParameters.ProjectWellHookupParameterId})`, this.wellHookupParameters));
    observableZip(...requests).subscribe(response => {
      this.wellHookupForm.reset();
      this.getWellHookupParameters(false);
    });
  }

  public cancelChanges(e): void {
    this.wellHookupForm.reset();
    this.getWellHookupParameters(false);
  }

  public setTDV(): void {
    let tdvState: boolean = true;
    this.tabTdv.emit(tdvState);
  }

  public clearTDV(): void {
    let tdvState: boolean = false;
    this.tabTdv.emit(tdvState);
  }

  private generateForm(): void {
    this.wellHookupForm = this.fb.group({
      CellarPadVisitTypeId: 4,
      AssociatedTankBatteryDesignId: null,
      IsLocatedOnTankBatteryPad: false,
      ProjectWellCount: null,
      ESPWellCount: null,
      ESP7WellCount: null,
      HPGLWellCount: null,
      ESPHRWellCount: null,
      GasLiftWellCount: null,
      BelowGradeWellCount: null,
      AboveGradeWellCount: null,
      DualFlowWellCount: null,
      WellToEdgeOfPadDistance: 0
    });
    this.wellHookupForm.valueChanges.subscribe(data => this.onValueChanged(data));
  }

  private onValueChanged(data?: any) {
    let isValid: boolean = false;

    if (!this.wellHookupForm) {
      return;
    }

    if (this.wellHookupForm.valid && this.wellHookupForm.dirty) {
      isValid = true;
      this.setTDV();
    } else {
      isValid = false;
      this.clearTDV();
    }
  }

  private getCellarPadVisitTypes() {
    this.lookupPlusService.getLookupNoCount('CellarPadVisitTypes').then(payload => {
      this.dCellarPadVisitTypes = payload.value;
    });
  }

  private getTankBatteryDesigns() {
    this.lookupPlusService.getLookupNoCount('TankBatteryDesigns').then(payload => {
      this.dTankBatteryDesigns = payload.value;
    });
  }

  public getWellHookupParameters(refreshParam: boolean): void {
    this.loading = true;
    this.estimationService.calculateWellHookupParameters(this.iProjectId(), refreshParam)
      .then(() => {
        this.lookupPlusService.getLookup('ProjectWellHookupParameters', `&$filter=ProjectId eq ${this.iProjectId()}`)
          .then(payload => {
            this.wellHookupParameters = payload.value[0];
            this.loading = false;
            if (this.wellHookupParameters == null)
              return;
            this.wellHookupForm.patchValue(this.wellHookupParameters);
          });
      });
  }
}